import { useCallback, useRef, useState } from "react";

import { Flex, Box } from "@chakra-ui/react";
import { useIntl } from "react-intl";
import { useShallow } from "zustand/react/shallow";

import { routes } from "config/routes";
import { BasicInformation } from "flows/Activities/components/TransportEquipment/BasicInformation";
import { DiscardDeliveryModal } from "flows/Activities/components/TransportEquipment/DiscardDeliveryModal";
import { DocumentPhoto } from "flows/Activities/components/TransportEquipment/DocumentPhoto";
import {
  EquipmentCountInfo,
  EquipmentList,
} from "flows/Activities/components/TransportEquipment/EquipmentList";
import {
  SubmissionSummary,
  SubmissionSummaryModal,
} from "flows/Activities/components/TransportEquipment/SubmissionSummaryModal";
import { useDiscardBlocker } from "flows/Activities/hooks/useDiscardBlocker";
import { useDeliverTransportEquipmentCountMutation } from "flows/Activities/queries/deliveryCheckin/transportEquipments.generated";
import { useDeliveryStore } from "flows/Activities/stores/useDeliveryStore";
import {
  getPhotoFileName,
  isPhotoTakenForTRTNumber,
  isValidTRTNumber,
} from "flows/Activities/utils/trtNumber";
import { Page } from "shared/components/Page";
import { useCountryCode } from "shared/hooks/useCountryCode";
import { useCustomToast } from "shared/hooks/useCustomToast";
import { useHubSlug } from "shared/hooks/useHubSlug";
import { Button } from "ui/Button/Button";
import { NavigationHeader } from "ui/NavigationHeader/NavigationHeader";

export function TransportEquipmentCountPage() {
  const intl = useIntl();
  const { showToastUI } = useCustomToast();
  const countryCode = useCountryCode();

  const hubSlug = useHubSlug();

  const { externalSupplierId, supplierId, deliveryTime } = useDeliveryStore(
    useShallow((state) => ({
      externalSupplierId: state.externalSupplierId,
      supplierId: state.supplierId,
      deliveryTime: state.deliveryTime,
    })),
  );

  const { isModalOpen, handleDiscard, handleNotDiscard, requestNavigation } = useDiscardBlocker({
    allowedPaths: [routes.activities.root],
  });

  const [pickupDate, setPickupDate] = useState(new Date().toISOString().split("T")[0]);
  const [TRTNumber, setTRTNumber] = useState("");
  const [documentPhotos, setDocumentPhotos] = useState<string[]>([]);
  const [summary, setSummary] = useState<SubmissionSummary | null>(null);

  const equipmentCountInfoRef = useRef<EquipmentCountInfo>({ lineItems: [], equipmentTypes: [] });
  const fileNamePrefix =
    hubSlug && isValidTRTNumber(TRTNumber) ? `${hubSlug}_${TRTNumber}` : undefined;

  const handleCountsChanged = useCallback((info: EquipmentCountInfo) => {
    equipmentCountInfoRef.current = info;
  }, []);

  const handlePhotosChanged = useCallback((photos: string[]) => {
    setDocumentPhotos(photos);
  }, []);

  const [deliverTransportEquipmentCount, { loading: isSubmittingEquipment }] =
    useDeliverTransportEquipmentCountMutation({
      onCompleted: () => {
        showToastUI({
          variant: "success",
          title: "components.activities.transport-equipment.count-page.submit.success.title",
        });
        requestNavigation(routes.activities.root);
      },
      onError: () => {
        showToastUI({
          title: "components.activities.transport-equipment.count-page.submit.error.title",
        });
      },
    });

  const submitCounts = () => {
    const { lineItems, transportEquipmentCountId } = equipmentCountInfoRef.current;
    deliverTransportEquipmentCount({
      variables: {
        input: {
          billNumber: TRTNumber,
          dateOfPickup: pickupDate,
          deliveryImageUrls: documentPhotos,
          externalSupplierId: externalSupplierId!,
          lineItems,
          transportEquipmentCountId,
        },
      },
    });
  };

  const handleSubmit = async () => {
    const deRestrictions =
      !isValidTRTNumber(TRTNumber) || !pickupDate || documentPhotos.length === 0;
    const nlRestrictions = !pickupDate;

    const specificCountryConditions = countryCode === "de" ? deRestrictions : nlRestrictions;

    if (!supplierId || !deliveryTime || specificCountryConditions) {
      showToastUI({
        title: "components.activities.transport-equipment.count-page.submit.validation.title",
      });
      return;
    }

    const { lineItems, equipmentTypes } = equipmentCountInfoRef.current;

    // Not loaded yet; submitting would send no line items at all.
    if (equipmentTypes.length === 0) {
      showToastUI({
        title: "components.activities.transport-equipment.count-page.submit.validation.title",
      });
      return;
    }

    const photoFileName = getPhotoFileName(documentPhotos[0]);
    const countsByTypeId = new Map(lineItems.map((li) => [li.equipmentTypeId, li]));

    setSummary({
      TRTNumber,
      pickupDate,
      lines: equipmentTypes.map(({ id, name, hasReceivedCounter }) => ({
        name,
        received: hasReceivedCounter ? (countsByTypeId.get(id)?.received ?? 0) : null,
        returned: countsByTypeId.get(id)?.returned ?? 0,
      })),
      photoFileName,
      isPhotoMatchingTRTNumber: isPhotoTakenForTRTNumber(photoFileName, TRTNumber),
    });
  };

  const handleConfirmSummary = () => {
    setSummary(null);
    submitCounts();
  };

  return (
    <Page bg="grey.100" isCentered isFull>
      <NavigationHeader
        title={intl.formatMessage({
          id: "components.activities.transport-equipment.count-page.header-title",
        })}
        onClickGoBack={() => requestNavigation(routes.activities.deliveryCheckIn.root)}
      />
      <Flex
        h="full"
        w="full"
        px="s200"
        py="s100"
        direction="column"
        overflow="auto"
        alignContent="flex-start"
      >
        <BasicInformation
          pickupDate={pickupDate}
          onPickupDateChange={setPickupDate}
          TRTNumber={TRTNumber}
          onTRTNumberChange={setTRTNumber}
        />
        <EquipmentList onCountsChanged={handleCountsChanged} />
        {countryCode === "de" && (
          <DocumentPhoto onPhotosChanged={handlePhotosChanged} fileName={fileNamePrefix} />
        )}
      </Flex>
      <Box bg="white" p="s200" w="full">
        <Button
          variant="primary"
          isFullWidth
          onClick={handleSubmit}
          isLoading={isSubmittingEquipment}
        >
          {intl.formatMessage({
            id: "components.activities.transport-equipment.count-page.cta",
          })}
        </Button>
      </Box>
      <DiscardDeliveryModal
        isOpen={isModalOpen}
        onClose={handleNotDiscard}
        onClickPrimary={handleDiscard}
        onClickSecondary={handleNotDiscard}
      />
      {summary && (
        <SubmissionSummaryModal
          isOpen
          summary={summary}
          onClose={() => setSummary(null)}
          onConfirmSubmit={handleConfirmSummary}
          onCancel={() => setSummary(null)}
        />
      )}
    </Page>
  );
}
